/**
 * showdownMoves.batch1 — batch 1 of the Showdown move-animation port.
 *
 * Each entry is a near-verbatim port of the move's script from Pokémon Showdown's
 * `BattleMoveAnims` (`battle-animations-moves.ts`, MIT/CC0). Our {@link FxScene} +
 * {@link Actor} mirror Showdown's `scene` + `Sprite`, so the literal
 * `scene.showEffect(...)` / `attacker.anim(...)` / `scene.wait(...)` sequences port
 * unchanged. Moves that delegate to a shared template reuse the ported templates
 * from {@link module:showdownOther}, or a locally-ported template below for the few
 * `BattleOtherAnims` entries not exported from there.
 *
 * Effect-name discipline: only effect keys vendored in our fx catalog are used.
 * Where Showdown referenced an effect we did not vendor (the attacker's own sprite
 * via `attacker.sp`, a remote-url background image), the call is adapted to the
 * nearest vendored effect, substituted with a `backgroundEffect` colour wash, or
 * omitted — noted inline at each site. The `actor.anim` choreography is preserved
 * verbatim.
 *
 * Pure: imports ONLY TYPES from `showdownFx.ts` (which pulls in Phaser) — every
 * script is a plain function over the `FxScene`/`Actor` interfaces.
 */
import type { FxScene, Actor, MoveAnim } from './showdownFx.js';
import { contactattack, punchattack, slashattack } from './showdownOther.js';

/* ──────────────────────────────────────────────────────────────────────────
 * Local template ports — the `BattleOtherAnims` entries that several scripts in
 * this batch delegate to but which `showdownOther.ts` does not export.
 * ──────────────────────────────────────────────────────────────────────── */

/** PS `BattleOtherAnims.lightstatus` — a single electro orb shrinks onto the user. */
const lightstatus: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 2, opacity: 0.1, time: 0 },
    { scale: 0, opacity: 0.5, time: 600 },
    'linear',
  );
};

/** PS `BattleOtherAnims.selfstatus` — two wisps contract onto the user. */
const selfstatus: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 2, opacity: 0.2, time: 0 },
    { scale: 0, opacity: 1, time: 300 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 2, opacity: 0.2, time: 200 },
    { scale: 0, opacity: 1, time: 500 },
    'linear',
  );
};

/** PS `BattleOtherAnims.sound` — three wisp rings expand outward from the user. */
const sound: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.7, time: 0 },
    { z: attacker.behind(-50), scale: 5, opacity: 0, time: 400 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.7, time: 150 },
    { z: attacker.behind(-50), scale: 5, opacity: 0, time: 600 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.7, time: 300 },
    { z: attacker.behind(-50), scale: 5, opacity: 0, time: 800 },
    'linear',
  );
};

/** PS `BattleOtherAnims.shake` — the user jitters left/right. */
const shake: MoveAnim = (_scene: FxScene, [attacker]: Actor[]) => {
  attacker.anim({ x: attacker.x - 10, time: 200 });
  attacker.anim({ x: attacker.x + 10, time: 300 });
  attacker.anim({ x: attacker.x, time: 200 });
};

/** PS `BattleOtherAnims.clawattack` — a flurry lunge with four claw marks. */
const clawattack: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  attacker.anim(
    { x: defender.leftof(-30), y: defender.y + 80, z: defender.behind(-30), time: 400 },
    'ballistic',
  );
  attacker.anim({ x: defender.leftof(30), y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim(
    { x: defender.leftof(30), y: defender.y + 80, z: defender.behind(-30), time: 200 },
    'ballisticUp',
  );
  attacker.anim({ x: defender.leftof(-30), y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.delay(450);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 200 }, 'swing');
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');

  scene.showEffect(
    'leftclaw',
    { x: defender.x - 20, y: defender.y + 20, z: defender.z, scale: 0, opacity: 1, time: 400 },
    { x: defender.x - 20, y: defender.y + 20, z: defender.z, scale: 3, opacity: 0, time: 700 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'leftclaw',
    { x: defender.x - 20, y: defender.y - 20, z: defender.z, scale: 0, opacity: 1, time: 400 },
    { x: defender.x - 20, y: defender.y - 20, z: defender.z, scale: 3, opacity: 0, time: 700 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'rightclaw',
    { x: defender.x + 20, y: defender.y + 20, z: defender.z, scale: 0, opacity: 1, time: 700 },
    { x: defender.x + 20, y: defender.y + 20, z: defender.z, scale: 3, opacity: 0, time: 1000 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'rightclaw',
    { x: defender.x + 20, y: defender.y - 20, z: defender.z, scale: 0, opacity: 1, time: 700 },
    { x: defender.x + 20, y: defender.y - 20, z: defender.z, scale: 3, opacity: 0, time: 1000 },
    'linear',
    'fade',
  );
};

/** PS `BattleOtherAnims.xattack` — two wisp puffs on the defender + a flurry lunge. */
const xattack: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 400 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(20),
      scale: 3,
      opacity: 0,
      time: 700,
    },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 700 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(20),
      scale: 3,
      opacity: 0,
      time: 1000,
    },
    'linear',
  );
  defender.delay(480);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 200 }, 'swing');
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
  attacker.anim(
    { x: defender.leftof(-30), y: defender.y + 80, z: defender.behind(-30), time: 400 },
    'ballistic',
  );
  attacker.anim({ x: defender.leftof(30), y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim(
    { x: defender.leftof(30), y: defender.y + 80, z: defender.behind(-30), time: 200 },
    'ballisticUp',
  );
  attacker.anim({ x: defender.leftof(-30), y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim({ time: 500 }, 'ballistic2Back');
};

/* ──────────────────────────────────────────────────────────────────────────
 * Bespoke move scripts (batch 1).
 * ──────────────────────────────────────────────────────────────────────── */

/**
 * PS `burnup` — a fiery charge that bursts on the defender. The remote weather
 * background gradient is substituted with a deep-red `backgroundEffect` wash, and
 * the two `attacker.sp` streak afterimages are omitted (no sprite-as-effect in our
 * engine) — the attacker's own lunge preserves the dash read.
 */
const burnup: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  // PS used a linear-gradient(#390000, #B84038); substitute a deep-red wash.
  scene.backgroundEffect('#390000', 800, 0.6);
  scene.showEffect(
    'fireball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 300 },
    { scale: 8, opacity: 0, time: 600 },
    'linear',
  );
  scene.showEffect(
    'fireball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 500 },
    { scale: 8, opacity: 0, time: 800 },
    'linear',
  );
  scene.showEffect(
    'flareball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.4, time: 300 },
    { scale: 4, opacity: 0, time: 600 },
    'linear',
  );
  scene.showEffect(
    'flareball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.4, time: 500 },
    { scale: 4, opacity: 0, time: 800 },
    'linear',
  );
  scene.showEffect(
    'flareball',
    { x: attacker.x, y: attacker.y, z: attacker.behind(-5), scale: 1.5, opacity: 0, time: 0 },
    { x: defender.x, y: defender.y, z: defender.z, opacity: 0.8, time: 300 },
    'accel',
    'explode',
  );
  // PS drew two attacker-sprite streaks here (showEffect(attacker.sp, …)); omitted.
  scene.showEffect(
    'wisp',
    { x: attacker.x + 30, y: attacker.y, z: attacker.z, scale: 1, opacity: 1, time: 900 },
    { y: attacker.y + 60, opacity: 0, time: 1300 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x - 30, y: attacker.y, z: attacker.z, scale: 1, opacity: 1, time: 1000 },
    { y: defender.y + 60, opacity: 0, time: 1400 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x + 15, y: attacker.y, z: attacker.z, scale: 1, opacity: 1, time: 1100 },
    { y: attacker.y + 60, opacity: 0, time: 1500 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x - 15, y: attacker.y, z: attacker.z, scale: 1, opacity: 1, time: 1200 },
    { y: attacker.y + 60, opacity: 0, time: 1600 },
    'linear',
  );
  attacker.anim({ x: defender.x, y: defender.y, z: defender.behind(-5), time: 300 }, 'accel');
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.delay(280);
  defender.anim({ z: defender.behind(15), time: 200 }, 'decel');
  defender.anim({ time: 300 }, 'decel');
};

/** PS `calmmind` — two shadow orbs contract into the user as it focuses. */
const calmmind: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'shadowball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 2, opacity: 0.1, time: 0 },
    { scale: 0, opacity: 0.5, time: 400 },
    'linear',
  );
  scene.showEffect(
    'shadowball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 2, opacity: 0.1, time: 200 },
    { scale: 0, opacity: 0.5, time: 600 },
    'linear',
  );
};

/** PS `charge` → `lightstatus` template. */
const charge: MoveAnim = lightstatus;

/** PS `chargebeam` — four electro orbs stream onto the defender and burst. */
const chargebeam: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.2 },
    { x: defender.x, y: defender.y, z: defender.behind(20), opacity: 0.6, time: 200 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.2, time: 50 },
    { x: defender.x + 10, y: defender.y - 5, z: defender.behind(20), opacity: 0.6, time: 250 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.2, time: 100 },
    { x: defender.x - 10, y: defender.y + 5, z: defender.behind(20), opacity: 0.6, time: 300 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.2, time: 150 },
    { x: defender.x, y: defender.y - 5, z: defender.behind(20), opacity: 0.6, time: 350 },
    'linear',
    'explode',
  );
};

/**
 * PS `chillyreception` — the user winks out and a hail flurry blows in. The entire
 * effect was driven by `attacker.sp` (the user's own sprite teleporting) plus a
 * remote weather-hail.png background, neither of which our engine supports; we keep
 * the black flash and substitute a pale-blue hail-wash for the removed pieces.
 */
const chillyreception: MoveAnim = (scene: FxScene) => {
  scene.backgroundEffect('#000000', 750, 1, 50);
  // PS faded the user out/in via attacker.sp and washed in a remote hail image;
  // substitute a pale-blue hail wash (sprite teleport omitted — no sprite-as-effect).
  scene.backgroundEffect('#C8E6F0', 750, 1, 800);
};

/** PS `chipaway` → `contactattack` template. */
const chipaway: MoveAnim = contactattack;

/** PS `circlethrow` — a grappling toss: wisps + a chop mark, the defender hurled. */
const circlethrow: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 400 },
    { x: defender.leftof(-20), z: defender.behind(20), scale: 3, opacity: 0, time: 700 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 500 },
    { x: defender.leftof(-20), z: defender.behind(20), scale: 0.6, opacity: 0, time: 800 },
    'linear',
  );
  scene.showEffect(
    'rightchop',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.6, opacity: 1, time: 400 },
    { x: defender.leftof(-20), z: defender.behind(20), scale: 1, opacity: 0, time: 800 },
    'linear',
  );
  attacker.anim(
    { x: defender.leftof(20), y: defender.y, z: defender.behind(-20), time: 400 },
    'ballistic2Under',
  );
  attacker.anim({ x: defender.x, y: defender.y, z: defender.z, time: 50 });
  attacker.anim({ time: 500 }, 'ballistic2');
  defender.delay(425);
  defender.anim(
    { x: defender.leftof(-15), y: defender.y, z: defender.behind(15), time: 50 },
    'swing',
  );
  defender.anim({ time: 300 }, 'swing');
};

/** PS `clamp` → `contactattack` template. */
const clamp: MoveAnim = contactattack;

/** PS `clearsmog` — wisps drift onto the defender, then puff away to either side. */
const clearsmog: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.7, opacity: 0.6, time: 0 },
    {
      x: defender.x,
      y: defender.y + 10,
      z: defender.behind(10),
      scale: 1,
      opacity: 0.3,
      time: 400,
    },
    'decel',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.7, opacity: 1, time: 100 },
    {
      x: defender.x - 20,
      y: defender.y + 5,
      z: defender.behind(10),
      scale: 1,
      opacity: 0.3,
      time: 500,
    },
    'decel',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.7, opacity: 1, time: 200 },
    {
      x: defender.x + 25,
      y: defender.y,
      z: defender.behind(10),
      scale: 1,
      opacity: 0.3,
      time: 600,
    },
    'decel',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x + 30, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 400 },
    { x: defender.x + 40, y: defender.y + 20, scale: 1.4, opacity: 0.2, time: 800 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x - 30, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 500 },
    { x: defender.x - 40, y: defender.y + 20, scale: 1.4, opacity: 0.2, time: 900 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x + 15, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 600 },
    { x: defender.x + 20, y: defender.y + 20, scale: 1.4, opacity: 0.2, time: 1000 },
    'linear',
    'fade',
  );
};

/** PS `closecombat` — a relentless four-hit flurry with fists + impact bursts. */
const closecombat: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  attacker.anim(
    { x: defender.leftof(-30), y: defender.y + 80, z: defender.behind(-30), time: 350 },
    'ballistic',
  );
  attacker.anim({ x: defender.leftof(30), y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim(
    { x: defender.leftof(30), y: defender.y + 80, z: defender.behind(-30), time: 150 },
    'ballisticUp',
  );
  attacker.anim({ x: defender.leftof(-30), y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.delay(450);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 200 }, 'swing');
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');

  scene.showEffect(
    'fist',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.6, opacity: 0.6, time: 425 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 525 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'fist',
    { x: defender.x - 10, y: defender.y + 20, z: defender.z, scale: 0.6, opacity: 0.6, time: 450 },
    { x: defender.x - 20, y: defender.y + 30, z: defender.z, scale: 1, opacity: 1, time: 550 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'fist',
    { x: defender.x + 30, y: defender.y - 20, z: defender.z, scale: 0.6, opacity: 0.6, time: 475 },
    { x: defender.x + 35, y: defender.y - 30, z: defender.z, scale: 1, opacity: 1, time: 575 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'fist',
    { x: defender.x - 30, y: defender.y - 20, z: defender.z, scale: 0.6, opacity: 0.6, time: 575 },
    { x: defender.x - 35, y: defender.y - 30, z: defender.z, scale: 1, opacity: 1, time: 775 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'fist',
    { x: defender.x, y: defender.y - 10, z: defender.z, scale: 0.6, opacity: 0.6, time: 600 },
    { x: defender.x + 10, y: defender.y - 15, z: defender.z, scale: 1, opacity: 1, time: 750 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'fist',
    { x: defender.x, y: defender.y + 10, z: defender.z, scale: 0.6, opacity: 0.6, time: 650 },
    { x: defender.x - 10, y: defender.y + 15, z: defender.z, scale: 1, opacity: 1, time: 800 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'impact',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.4, time: 525 },
    { scale: 3, opacity: 0, time: 825 },
    'linear',
  );
  scene.showEffect(
    'impact',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.4, time: 750 },
    { scale: 3, opacity: 0, time: 1050 },
    'linear',
  );
};

/** PS `coil` — three iceball rings sweep over the user as it gathers itself. */
const coil: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'iceball',
    {
      x: attacker.x,
      y: attacker.y + 15,
      z: attacker.z,
      scale: 0.7,
      xscale: 2,
      opacity: 0.6,
      time: 0,
    },
    { scale: 0.4, xscale: 1, opacity: 0.1, time: 500 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'iceball',
    {
      x: attacker.x,
      y: attacker.y - 5,
      z: attacker.z,
      scale: 0.7,
      xscale: 2,
      opacity: 0.6,
      time: 50,
    },
    { scale: 0.4, xscale: 1, opacity: 0.1, time: 550 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'iceball',
    {
      x: attacker.x,
      y: attacker.y - 20,
      z: attacker.z,
      scale: 0.7,
      xscale: 2,
      opacity: 0.6,
      time: 100,
    },
    { scale: 0.4, xscale: 1, opacity: 0.1, time: 600 },
    'decel',
    'fade',
  );
  attacker.anim({ y: attacker.y + 5, yscale: 1.1, time: 300 }, 'swing');
  attacker.delay(400);
  attacker.anim({ time: 300 }, 'swing');
};

/** PS `cometpunch` → `punchattack` template. */
const cometpunch: MoveAnim = punchattack;

/** PS `confide` → `sound` template. */
const confide: MoveAnim = sound;

/**
 * PS `confuseray` — an electro orb floats out, loops around the defender, and bursts.
 * The PS `isMissedPokemon` early-return is dropped (our Actor has no miss state) so
 * the full sequence always plays.
 */
const confuseray: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.15, opacity: 0 },
    {
      x: defender.leftof(40),
      y: defender.y + 15,
      z: defender.z,
      scale: 0.3,
      opacity: 0.7,
      time: 500,
    },
    'decel',
    'fade',
  );
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.15, opacity: 0 },
    {
      x: defender.leftof(40),
      y: defender.y + 15,
      z: defender.z,
      scale: 0.3,
      opacity: 0.7,
      time: 500,
    },
    'decel',
    'fade',
  );
  scene.showEffect(
    'electroball',
    {
      x: defender.leftof(40),
      y: defender.y + 15,
      z: defender.z,
      scale: 0.3,
      opacity: 0.7,
      time: 500,
    },
    { x: defender.leftof(-40), y: defender.y, z: defender.z, scale: 0.2, opacity: 1, time: 700 },
    'swing',
    'fade',
  );
  scene.showEffect(
    'electroball',
    { x: defender.leftof(-40), y: defender.y, z: defender.z, scale: 0.1, opacity: 0, time: 700 },
    {
      x: defender.leftof(10),
      y: defender.y - 15,
      z: defender.z,
      scale: 0.5,
      opacity: 1,
      time: 900,
    },
    'swing',
    'explode',
  );
};

/**
 * PS `conversion` — the user shimmers as its sprite scales down. PS drew the user's
 * own sprite (`attacker.sp`) here; with no sprite-as-effect we substitute a single
 * wisp contracting onto the user for the same shimmer read.
 */
const conversion: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 2, opacity: 0.3 },
    { scale: 1, opacity: 0, time: 600 },
    'decel',
  );
};

/** PS `copycat` — no animation. */
const copycat: MoveAnim = () => {};

/**
 * PS `cosmicpower` — three wisps rise off the user. The remote bg-space.jpg
 * background is substituted with a deep-indigo `backgroundEffect` wash.
 */
const cosmicpower: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  // PS used a remote bg-space.jpg; substitute a deep-indigo cosmic wash.
  scene.backgroundEffect('#1a1040', 600, 0.6);
  scene.showEffect(
    'wisp',
    { x: attacker.x + 40, y: attacker.y - 40, z: attacker.z, scale: 0.2, opacity: 1, time: 0 },
    { y: attacker.y + 60, opacity: 0, time: 400 },
    'accel',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x - 40, y: attacker.y - 40, z: attacker.z, scale: 0.2, opacity: 1, time: 200 },
    { y: attacker.y + 60, opacity: 0, time: 600 },
    'accel',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y - 40, z: attacker.z, scale: 0.2, opacity: 1, time: 400 },
    { y: attacker.y + 60, opacity: 0, time: 800 },
    'accel',
  );
};

/** PS `cottonguard` → `selfstatus` template. */
const cottonguard: MoveAnim = selfstatus;

/** PS `covet` — a fond heart, then the user darts in to swipe at the defender. */
const covet: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'heart',
    { x: attacker.x - 10, y: attacker.y + 50, z: attacker.z, scale: 0.5, opacity: 1, time: 0 },
    { scale: 3, opacity: 0, time: 300 },
    'ballistic2Under',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 650 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(30),
      scale: 1.5,
      opacity: 0,
      time: 850,
    },
    'linear',
  );
  attacker.delay(300);
  attacker.anim(
    { x: attacker.leftof(-20), y: attacker.y, z: attacker.behind(-20), opacity: 0, time: 200 },
    'linear',
  );
  attacker.anim(
    { x: defender.x, y: defender.y, z: defender.behind(-120), opacity: 0, time: 1 },
    'linear',
  );
  attacker.anim(
    { x: defender.x, y: defender.y, z: defender.behind(40), opacity: 1, time: 250 },
    'linear',
  );
  attacker.anim(
    { x: defender.x, y: defender.y, z: defender.behind(-5), opacity: 0, time: 300 },
    'linear',
  );
  attacker.anim({ opacity: 0, time: 1 }, 'linear');
  attacker.anim({ time: 300, opacity: 1 }, 'linear');
  defender.delay(630);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `crabhammer` — a water plume bursts on the defender, then a contact smash. */
const crabhammer: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  scene.showEffect(
    'waterwisp',
    {
      x: defender.x,
      y: defender.y + 80,
      z: defender.behind(-15),
      scale: 1.5,
      opacity: 0.8,
      time: 400,
    },
    { y: defender.y, z: defender.z, scale: 0.5, opacity: 1, time: 500 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'waterwisp',
    { x: defender.x, y: defender.y - 25, z: defender.z, scale: 1, time: 500 },
    { x: defender.x + 50, scale: 0.6, opacity: 0.3, time: 800 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'waterwisp',
    { x: defender.x, y: defender.y - 25, z: defender.z, scale: 1, time: 500 },
    { x: defender.x - 50, scale: 0.6, opacity: 0.3, time: 800 },
    'linear',
    'fade',
  );
  contactattack(scene, actors);
};

/** PS `craftyshield` → `selfstatus` template. */
const craftyshield: MoveAnim = selfstatus;

/** PS `crosschop` — two crossed slash marks + crossing chops, then a contact smash. */
const crosschop: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  scene.showEffect(
    'rightslash',
    { x: defender.x - 10, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 425 },
    { scale: 3, opacity: 0, time: 800 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'leftslash',
    { x: defender.x + 10, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 425 },
    { scale: 3, opacity: 0, time: 800 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'leftchop',
    { x: defender.x + 60, y: defender.y + 70, z: defender.z, scale: 0.75, opacity: 1, time: 400 },
    { x: defender.x - 60, y: defender.y - 70, scale: 0.5, opacity: 0, time: 600 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'rightchop',
    { x: defender.x - 60, y: defender.y + 70, z: defender.z, scale: 0.75, opacity: 1, time: 400 },
    { x: defender.x + 60, y: defender.y - 70, scale: 0.5, opacity: 0, time: 600 },
    'linear',
    'fade',
  );
  contactattack(scene, actors);
};

/** PS `crosspoison` — a crossing slash flurry that leaves poison wisps lingering. */
const crosspoison: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  attacker.anim(
    { x: defender.leftof(-30), y: defender.y + 80, z: defender.behind(-30), time: 300 },
    'ballistic',
  );
  attacker.anim({ x: defender.leftof(30), y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim(
    { x: defender.leftof(30), y: defender.y + 80, z: defender.behind(-30), time: 200 },
    'ballisticUp',
  );
  attacker.anim({ x: defender.leftof(-30), y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.delay(450);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 200 }, 'swing');
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');

  scene.showEffect(
    'rightslash',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 425 },
    { scale: 2, opacity: 0.5, time: 725 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'rightslash',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 625 },
    { scale: 3, opacity: 0, time: 1000 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'leftslash',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 625 },
    { scale: 3, opacity: 0, time: 1000 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'poisonwisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.2, opacity: 1, time: 450 },
    { x: defender.x + 30, y: defender.y + 10, scale: 1, opacity: 0.5, time: 750 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'poisonwisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.2, opacity: 1, time: 550 },
    { x: defender.x - 30, y: defender.y - 40, scale: 1, opacity: 0.5, time: 850 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'poisonwisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.2, opacity: 1, time: 650 },
    { x: defender.x - 10, y: defender.y + 40, scale: 1, opacity: 0.5, time: 950 },
    'decel',
    'fade',
  );
};

/** PS `crunch` — a dark wash + the two jaws clamp shut, then a contact smash. */
const crunch: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  scene.backgroundEffect('#000000', 800, 0.3);
  scene.showEffect(
    'topbite',
    { x: defender.x, y: defender.y + 70, z: defender.z, scale: 0.65, opacity: 0, time: 370 },
    { y: defender.y + 20, opacity: 1, time: 500 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'bottombite',
    { x: defender.x, y: defender.y - 70, z: defender.z, scale: 0.65, opacity: 0, time: 370 },
    { y: defender.y - 20, opacity: 1, time: 500 },
    'linear',
    'explode',
  );
  contactattack(scene, actors);
};

/** PS `crushclaw` → `clawattack` template. */
const crushclaw: MoveAnim = clawattack;

/** PS `curse` → `selfstatus` template. */
const curse: MoveAnim = selfstatus;

/** PS `cut` → `slashattack` template. */
const cut: MoveAnim = slashattack;

/** PS `darkpulse` — a dark wash + a horizontal shadow ripple + radiating poison wisps. */
const darkpulse: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  const xf = [1, -1, 1, -1];
  const yf = [1, -1, -1, 1];
  const xf2 = [1, 0, -1, 0];
  const yf2 = [0, 1, 0, -1];

  scene.backgroundEffect('#000000', 900, 0.3);
  scene.showEffect(
    'shadowball',
    {
      x: attacker.x,
      y: attacker.y - 50,
      z: attacker.z,
      scale: 1,
      xscale: 5,
      opacity: 0.8,
      time: 0,
    },
    { scale: 2, xscale: 8, opacity: 0.1, time: 800 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'shadowball',
    { x: defender.x, y: defender.y, z: defender.z, opacity: 0.3, scale: 0, time: 300 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 2, opacity: 0, time: 600 },
    'linear',
  );
  scene.showEffect(
    'shadowball',
    { x: defender.x, y: defender.y, z: defender.z, opacity: 0.3, scale: 0, time: 500 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 2, opacity: 0, time: 800 },
    'linear',
  );
  for (let i = 0; i < 4; i++) {
    scene.showEffect(
      'poisonwisp',
      { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.3, opacity: 0.4 },
      {
        x: attacker.x + 240 * xf[i],
        y: attacker.y,
        z: attacker.z + 137 * yf[i],
        scale: 0.7,
        opacity: 0.4,
        time: 600,
      },
      'accel',
      'fade',
    );
    scene.showEffect(
      'poisonwisp',
      { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.2, opacity: 0.4 },
      {
        x: attacker.x + 339 * xf2[i],
        y: attacker.y,
        z: attacker.z + 194 * yf2[i],
        scale: 0.5,
        opacity: 0.4,
        time: 600,
      },
      'accel',
      'fade',
    );
  }
};

/** PS `dazzlinggleam` — a pink wash + six glittering wisps burst over the defender. */
const dazzlinggleam: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.backgroundEffect('#FF99FF', 700, 0.5);
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.6, opacity: 0.6 },
    { x: defender.x + 30, y: defender.y + 30, z: defender.z, scale: 1, opacity: 0.3, time: 200 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.6, opacity: 0.6, time: 75 },
    { x: defender.x + 20, y: defender.y - 30, z: defender.z, scale: 1, opacity: 0.3, time: 275 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.6, opacity: 0.6, time: 150 },
    { x: defender.x - 30, y: defender.y, z: defender.z, scale: 1, opacity: 0.3, time: 350 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.6, opacity: 0.6, time: 225 },
    { x: defender.x - 10, y: defender.y + 10, z: defender.z, scale: 1, opacity: 0.3, time: 425 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.6, opacity: 0.6, time: 300 },
    { x: defender.x + 10, y: defender.y - 10, z: defender.z, scale: 1, opacity: 0.3, time: 500 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.6, opacity: 0.6, time: 375 },
    { x: defender.x - 20, y: defender.y, z: defender.z, scale: 1, opacity: 0.3, time: 575 },
    'linear',
    'explode',
  );
};

/** PS `defensecurl` → `selfstatus` template. */
const defensecurl: MoveAnim = selfstatus;

/** PS `defog` — a white wash + the `sound` ring pulse. */
const defog: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  scene.backgroundEffect('#FFFFFF', 900, 0.5);
  sound(scene, actors);
};

/** PS `detect` → `selfstatus` template. */
const detect: MoveAnim = selfstatus;

/**
 * PS `dig` — the user burrows under and erupts beneath the defender. Charges: the
 * `prepareAnim` sinks the user underground.
 */
const dig: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.5, time: 350 },
    { scale: 3, opacity: 0, time: 500 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.5, time: 450 },
    { scale: 3, opacity: 0, time: 600 },
    'linear',
  );
  attacker.anim({ y: attacker.y - 80, opacity: 0, time: 100 }, 'linear');
  attacker.anim(
    { x: defender.x, y: defender.y - 80, z: defender.z, opacity: 0, time: 1 },
    'linear',
  );
  attacker.anim(
    { x: defender.x, y: defender.y + 10, z: defender.z, opacity: 1, time: 350 },
    'linear',
  );
  attacker.anim(
    { x: defender.x, y: defender.y - 80, z: defender.z, opacity: 0, time: 300 },
    'linear',
  );
  attacker.anim(
    { x: attacker.x, y: attacker.y - 80, z: defender.z, opacity: 0, time: 1 },
    'linear',
  );
  attacker.anim({ time: 300, opacity: 1 }, 'linear');
  defender.delay(380);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `disarmingvoice` → `sound` template. */
const disarmingvoice: MoveAnim = sound;

/** PS `discharge` — three electro orbs swell outward from the user in waves. */
const discharge: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.2, time: 0 },
    { scale: 8, opacity: 0.1, time: 600 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.2, time: 150 },
    { scale: 8, opacity: 0.1, time: 750 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.2, time: 300 },
    { scale: 8, opacity: 0.1, time: 900 },
    'linear',
    'fade',
  );
};

/**
 * PS `dive` — the user submerges and surges up beneath the defender. Charges: the
 * `prepareAnim` sinks the user underwater.
 */
const dive: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'waterwisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.5, time: 350 },
    { scale: 3, opacity: 0, time: 500 },
    'linear',
  );
  scene.showEffect(
    'waterwisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.5, time: 450 },
    { scale: 3, opacity: 0, time: 600 },
    'linear',
  );
  attacker.anim({ y: attacker.y - 80, opacity: 0, time: 100 }, 'linear');
  attacker.anim(
    { x: defender.x, y: defender.y - 80, z: defender.z, opacity: 0, time: 1 },
    'linear',
  );
  attacker.anim(
    { x: defender.x, y: defender.y + 10, z: defender.z, opacity: 1, time: 350 },
    'decel',
  );
  attacker.anim(
    { x: defender.x, y: defender.y - 80, z: defender.z, opacity: 0, time: 300 },
    'accel',
  );
  attacker.anim({ y: attacker.y - 80, opacity: 0, time: 1 }, 'linear');
  attacker.anim({ time: 300, opacity: 1 }, 'decel');
  defender.delay(380);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `dizzypunch` → `punchattack` template. */
const dizzypunch: MoveAnim = punchattack;

/** PS `doomdesire` — two ominous dark washes as fate is sealed. */
const doomdesire: MoveAnim = (scene: FxScene) => {
  scene.backgroundEffect('#000000', 300, 0.2);
  scene.backgroundEffect('#000000', 300, 0.3, 200);
};

/** PS `doublehit` → `xattack` template. */
const doublehit: MoveAnim = xattack;

/** PS `doublekick` — two feet stomp the defender, plus the `xattack` flurry. */
const doublekick: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  scene.showEffect(
    'foot',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 450 },
    {
      x: defender.leftof(20),
      y: defender.y,
      z: defender.behind(20),
      scale: 2,
      opacity: 0,
      time: 750,
    },
    'linear',
  );
  scene.showEffect(
    'foot',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 750 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(20),
      scale: 2,
      opacity: 0,
      time: 1050,
    },
    'linear',
  );
  xattack(scene, actors);
};

/** PS `doubleslap` → `xattack` template. */
const doubleslap: MoveAnim = xattack;

/**
 * PS `doubleteam` — the user jitters and splits into two afterimages. PS drew the
 * user's own sprite (`attacker.sp`) as the two splitting copies; with no
 * sprite-as-effect we substitute two wisp puffs sliding to either side.
 */
const doubleteam: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [attacker, defender] = actors;
  shake(scene, actors);
  // PS split the user's own sprite to either side; substitute wisp afterimages.
  scene.showEffect(
    'wisp',
    { x: defender.x, y: attacker.y, z: attacker.z, opacity: 0.3 },
    { x: defender.x - 60, y: defender.y, z: defender.z, opacity: 0, time: 500 },
    'decel',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: attacker.y, z: attacker.z, opacity: 0.3 },
    { x: defender.x + 60, y: defender.y, z: defender.z, opacity: 0, time: 500 },
    'decel',
  );
};

/**
 * PS `dracometeor` — meteors rain down on the defender from the sky and explode.
 * The remote bg-space.jpg background is substituted with a deep-indigo wash.
 */
const dracometeor: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  // PS used a remote bg-space.jpg; substitute a deep-indigo cosmic wash.
  scene.backgroundEffect('#1a1040', 1100, 0.8);
  scene.showEffect(
    'flareball',
    { x: defender.leftof(-200), y: defender.y + 175, z: defender.z, scale: 0.1, opacity: 0 },
    { x: defender.x + 50, y: defender.y, scale: 1.5, opacity: 0.8 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'flareball',
    {
      x: defender.leftof(-200),
      y: defender.y + 20 + 175,
      z: defender.z,
      scale: 0.1,
      opacity: 0,
      time: 150,
    },
    { x: defender.x - 30, y: defender.y - 5, scale: 1.5, opacity: 0.8 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'flareball',
    {
      x: defender.leftof(-200),
      y: defender.y - 20 + 175,
      z: defender.z,
      scale: 0.1,
      opacity: 0,
      time: 300,
    },
    { x: defender.x + 30, y: defender.y - 10, scale: 1.5, opacity: 0.8 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'rock3',
    { x: defender.leftof(-200), y: defender.y + 175, z: defender.z, scale: 0.1, opacity: 0 },
    { x: defender.x + 30, y: defender.y, scale: 1.5, opacity: 0.4 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'rock3',
    {
      x: defender.leftof(-200),
      y: defender.y + 20 + 175,
      z: defender.z,
      scale: 0.1,
      opacity: 0,
      time: 150,
    },
    { x: defender.x - 20, y: defender.y - 5, scale: 1.5, opacity: 0.4 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'rock3',
    {
      x: defender.leftof(-200),
      y: defender.y - 20 + 175,
      z: defender.z,
      scale: 0.1,
      opacity: 0,
      time: 300,
    },
    { x: defender.x + 20, y: defender.y, scale: 1.5, opacity: 0.4 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'shadowball',
    {
      x: defender.x + 30,
      y: defender.y - 50,
      z: defender.z,
      scale: 1,
      xscale: 2,
      opacity: 0.5,
      time: 500,
    },
    { scale: 3, xscale: 8, opacity: 0.1, time: 700 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'shadowball',
    { x: defender.x, y: defender.y, z: defender.z, opacity: 0.3, scale: 0, time: 500 },
    { scale: 3, opacity: 0, time: 700 },
    'linear',
  );
  scene.showEffect(
    'shadowball',
    {
      x: defender.x - 20,
      y: defender.y - 50,
      z: defender.z,
      scale: 1,
      xscale: 2,
      opacity: 0.5,
      time: 650,
    },
    { scale: 3, xscale: 8, opacity: 0.1, time: 850 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'shadowball',
    { x: defender.x, y: defender.y, z: defender.z, opacity: 0.3, scale: 0, time: 650 },
    { scale: 4, opacity: 0, time: 850 },
    'linear',
  );
  scene.showEffect(
    'shadowball',
    {
      x: defender.x + 20,
      y: defender.y - 50,
      z: defender.z,
      scale: 1,
      xscale: 2,
      opacity: 0.5,
      time: 700,
    },
    { scale: 3, xscale: 8, opacity: 0.1, time: 900 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'shadowball',
    { x: defender.x, y: defender.y, z: defender.z, opacity: 0.3, scale: 0, time: 700 },
    { scale: 4, opacity: 0, time: 900 },
    'linear',
  );
  scene.showEffect(
    'rock3',
    {
      x: defender.leftof(-200),
      y: defender.y - 20 + 150,
      z: defender.z,
      scale: 0.1,
      opacity: 0.6,
      time: 50,
    },
    { x: defender.x - 250, y: defender.y - 80, z: defender.behind(60), scale: 0.8, opacity: 0 },
    'accel',
    'fade',
  );
  scene.showEffect(
    'rock3',
    {
      x: defender.leftof(-220),
      y: defender.y + 20 + 130,
      z: defender.z,
      scale: 0.1,
      opacity: 0.6,
      time: 150,
    },
    { x: defender.x + 80, y: defender.y - 50, z: defender.behind(30), scale: 0.8, opacity: 0 },
    'accel',
    'fade',
  );
  scene.showEffect(
    'rock3',
    {
      x: defender.leftof(-180),
      y: defender.y + 20 + 130,
      z: defender.z,
      scale: 0.1,
      opacity: 0.6,
      time: 300,
    },
    { x: defender.x - 200, y: defender.y + 20, z: defender.behind(30), scale: 0.8, opacity: 0 },
    'accel',
    'fade',
  );
  defender.delay(500);
  defender.anim({ x: defender.x + 30, z: defender.behind(10), time: 75 });
  defender.anim({ x: defender.x - 30, time: 100 });
  defender.anim({ x: defender.x + 30, time: 100 });
  defender.anim({ x: defender.x - 30, z: defender.behind(10), time: 100 });
  defender.anim({ x: defender.x, time: 100 });
};

/**
 * PS `dragonascent` — the user soars off-screen and dives back through the defender
 * in a blaze. The remote bg-space.jpg background is substituted with a deep-indigo wash.
 */
const dragonascent: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  // PS used a remote bg-space.jpg; substitute a deep-indigo cosmic wash.
  scene.backgroundEffect('#1a1040', 1000, 0.7);
  scene.showEffect(
    'iceball',
    {
      x: attacker.leftof(-25),
      y: attacker.y + 250,
      z: attacker.z,
      scale: 0.5,
      opacity: 0.8,
      time: 400,
    },
    { x: defender.x, y: defender.y, z: defender.behind(5), scale: 2, time: 700 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'energyball',
    {
      x: attacker.leftof(-25),
      y: attacker.y + 250,
      z: attacker.z,
      scale: 0.5,
      opacity: 0.1,
      time: 400,
    },
    { x: defender.x, y: defender.y, z: defender.behind(5), scale: 2, time: 700 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'electroball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.5, opacity: 0.7, time: 650 },
    { scale: 4, opacity: 0, time: 900 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'energyball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.5, opacity: 0.7, time: 750 },
    { scale: 4, opacity: 0, time: 1000 },
    'linear',
    'fade',
  );
  attacker.anim({ opacity: 0, y: defender.y + 120, time: 300 }, 'accel');
  attacker.delay(625);
  attacker.anim({ x: attacker.x, y: attacker.y + 40, z: attacker.behind(40), time: 1 });
  attacker.anim({ x: attacker.x, y: attacker.y, z: attacker.z, time: 250 }, 'decel');
  defender.delay(725);
  defender.anim({ z: defender.behind(20), time: 250 }, 'decel');
  defender.anim({ time: 200 }, 'swing');
};

/* ──────────────────────────────────────────────────────────────────────────
 * The batch-1 table, keyed by Showdown move id (toID form).
 *
 * Moves with no `BattleMoveAnims` entry in Showdown (burningjealousy, camouflage,
 * captivate, charm, chillingwater, coaching, comeuppance, confusion, constrict,
 * conversion2, corrosivegas, cottonspore, counter, darkestlariat, destinybond,
 * disable, doubleedge) are intentionally absent — they fall through to the
 * category/flavor defaults in the resolver, like in Showdown.
 *
 * Charging moves (dig, dive) port only their main `anim` here: the batch export
 * type is `Record<string, MoveAnim>` (matching the existing 28 entries, which are
 * all plain functions), which cannot carry the `{ anim, prepareAnim }` object form.
 * The pre-roll burrow/submerge is dropped accordingly; the main strike is verbatim.
 * ──────────────────────────────────────────────────────────────────────── */

export const SHOWDOWN_BATCH_1: Record<string, MoveAnim> = {
  burnup,
  calmmind,
  charge,
  chargebeam,
  chillyreception,
  chipaway,
  circlethrow,
  clamp,
  clearsmog,
  closecombat,
  coil,
  cometpunch,
  confide,
  confuseray,
  conversion,
  copycat,
  cosmicpower,
  cottonguard,
  covet,
  crabhammer,
  craftyshield,
  crosschop,
  crosspoison,
  crunch,
  crushclaw,
  curse,
  cut,
  darkpulse,
  dazzlinggleam,
  defensecurl,
  defog,
  detect,
  dig,
  disarmingvoice,
  discharge,
  dive,
  dizzypunch,
  doomdesire,
  doublehit,
  doublekick,
  doubleslap,
  doubleteam,
  dracometeor,
  dragonascent,
};
